import {
  ElementOriginMapping,
  ImportRowDraft,
  MappingReferenceType,
  ResourceCodeMappingEntry,
} from '@shared/models/element-import.model';
import { ConversionFactorUnit } from '@shared/models/unit.model';
import { IProduct } from '@shared/models/product.model';
import { Recipe } from '@shared/models/recipe.model';
import { CandidateRow } from '@shared/models/element-import.model';
import { ElementKind, IElement } from '@shared/models/project.model';
import {
  ElementQuantityExpressionName,
  elementListSelectableQuantityNames,
  elementQuantityUnitRecord,
  PIECES_QUANTITY_NAME,
} from '@shared/models/element-quantities.model';
import {
  candidateConversion,
  candidateQuantityFrom,
  candidateUnitFrom,
  getProductConvertibleQuantityNames,
  isIdentityConversionFactor,
  resolveProductQuantityName,
} from '@shared/helpers/materialize-candidate.helpers';
import { type IfcMeasure } from '@shared/helpers/ifc-quantity.helpers';
import {
  resolveImportQuantity,
  type ImportTarget,
} from '@shared/helpers/import-quantity-resolution.helpers';
import {
  canConvertConversionFactors,
  convertCount,
  getCorrespondingConversionFactorUnit,
} from '@shared/helpers/conversion.helpers';
import { isElementQuantityExpressionName } from '@shared/helpers/element-quantity.helpers';
import { assertUnreachable } from '@shared/helpers/function.helpers';
import {
  getElementQuantityProperties,
  getSelectedElementQuantityProperty,
} from '@shared/helpers/element-quantity-core.helpers';
import { createElementWithCategory } from '@shared/helpers/element-category.helpers';

/**
 * Map the quantity + unit a user typed in-row to a `ResourceCodeMappingEntry`.
 *
 * The CSV row contributes `qtyFrom` (e.g. 3) and `unitFrom` (e.g. "frp").
 * The user types a target value (e.g. 1.5 kg). We persist the per-unit
 * conversion as `{ unitTo, quantityFactor }` keyed by `unitFrom`; downstream
 * materialization rebuilds the element expression as `qtyFrom * quantityFactor`.
 *
 * When the product's own conversion factors already cover `unitFrom` (see
 * {@link candidateConversion}), no manual factor is required — return `{}`.
 * That mirrors the side-panel form's auto-conversion branch.
 *
 * Returns `null` when inputs are unusable (missing qtyFrom, qtyTo ≤ 0, NaN).
 */
export const deriveMappingEntryFromQtyTo = (params: {
  row: CandidateRow;
  product: IProduct | undefined;
  qtyTo: number;
  unitTo: string;
}): ResourceCodeMappingEntry | null => {
  const { row, product, qtyTo, unitTo } = params;

  // Auto-conversion path: product already converts unitFrom -> product.unit.
  if (product && candidateConversion(row, product)) {
    return {};
  }

  const qtyFrom = candidateQuantityFrom(row);
  if (qtyFrom === undefined || qtyFrom === 0) return null;

  if (!Number.isFinite(qtyTo) || qtyTo <= 0) return null;

  const quantityFactor = qtyTo / qtyFrom;
  if (!Number.isFinite(quantityFactor) || quantityFactor <= 0) return null;

  return {
    unitTo: (unitTo || undefined) as ConversionFactorUnit | undefined,
    quantityFactor,
  };
};

/**
 * Inverse of {@link deriveMappingEntryFromQtyTo}: given a saved mapping entry
 * and the CSV row's qtyFrom, return the qtyTo value that the in-row field
 * should display.
 */
export const inverseQtyToFromEntry = (params: {
  row: CandidateRow;
  entry: ResourceCodeMappingEntry | undefined;
}): number | undefined => {
  const { row, entry } = params;
  const qtyFrom = candidateQuantityFrom(row);
  if (qtyFrom === undefined) return undefined;
  if (!entry || entry.quantityFactor === undefined) return undefined;
  const qtyTo = qtyFrom * entry.quantityFactor;
  return Number.isFinite(qtyTo) ? qtyTo : undefined;
};

/** Convenience: the CSV unit string the user is mapping from, if any. */
export const inRowUnitFrom = (row: CandidateRow): string =>
  candidateUnitFrom(row) ?? '';

/**
 * Element quantities the user can map a row to: the icon quantities whose unit
 * the mapped product can convert to, plus {@link PIECES_QUANTITY_NAME}.
 * A product without an `m` conversion factor never offers "length" — so those
 * picks stay convertible. Pieces is hidden and not an icon quantity, but the
 * import menu must still offer it and persist `quantityName: pieces` even when
 * the product has no `pcs` factor (the user types the count).
 */
export const getSelectableQuantityNames = (
  product: IProduct | undefined,
): ElementQuantityExpressionName[] => {
  if (!product) return [];
  const convertible = getProductConvertibleQuantityNames(product);
  if (convertible.includes(PIECES_QUANTITY_NAME)) return convertible;
  return [...convertible, PIECES_QUANTITY_NAME];
};

/**
 * Element quantities a recipe row can map to (DEV-3387): the list-selectable
 * quantities the recipe's category actually defines, including pieces.
 * A recipe carries no conversion factors, so (unlike the product path) any of
 * its category's quantities is addressable — the CSV Mängd is written straight
 * into the picked one. Service categories have no pieces row, so they omit it.
 */
export const getSelectableQuantityNamesForRecipe = (
  recipe: Recipe | undefined,
): ElementQuantityExpressionName[] => {
  if (!recipe) return [];
  const element = createElementWithCategory({
    kind: ElementKind.Element,
    category_id: recipe.category_id,
  });
  const available = new Set(
    getElementQuantityProperties(element).map((p) => p.name),
  );
  return elementListSelectableQuantityNames
    .filter(isElementQuantityExpressionName)
    .filter((name) => available.has(name));
};

/** Element quantities a mapping target can address (DEV-3456). */
export const getSelectableQuantityNamesForTarget = (params: {
  referenceType: MappingReferenceType;
  product: IProduct | undefined;
  recipe: Recipe | undefined;
}): ElementQuantityExpressionName[] =>
  params.referenceType === 'recipe'
    ? getSelectableQuantityNamesForRecipe(params.recipe)
    : getSelectableQuantityNames(params.product);

/**
 * True when a row's existing calibration still means something after switching
 * to a new mapping target — i.e. the target can address the quantity the factor
 * was calibrated for (DEV-3456).
 *
 * Switching product or recipe used to drop the calibration unconditionally,
 * which threw away a perfectly valid m² rate when moving between two walls.
 * Compatibility is a property of the units, not of the target's kind: a steel
 * product and a wall recipe both address `area_side`, so a factor calibrated
 * for it survives either way.
 */
export const isMappingCalibrationCompatible = (params: {
  entry: Pick<ResourceCodeMappingEntry, 'unitTo' | 'quantityName'> | undefined;
  referenceType: MappingReferenceType;
  product: IProduct | undefined;
  recipe: Recipe | undefined;
}): boolean => {
  const { unitTo, quantityName } = params.entry ?? {};
  // Nothing calibrated: keeping and wiping are the same thing.
  if (!unitTo && !quantityName) return true;

  const names = getSelectableQuantityNamesForTarget(params);
  // An explicit pick is the strongest signal — it must survive verbatim.
  if (quantityName) return names.includes(quantityName);
  return names.some((name) => elementQuantityUnitRecord[name] === unitTo);
};

/**
 * Build the mapping entry that writes a row's Mängd to a chosen element
 * quantity. The CSV unit (e.g. "st") need not exist in the product's conversion
 * factors — the selection is about which element quantity gets filled, and the
 * factor converts the CSV count into that quantity's unit.
 *
 * `unitTo` is the picked quantity's conversion-factor unit; `quantityFactor` is
 * calibrated so `qtyFrom * quantityFactor` is the value in that unit. It is
 * derived, in priority order, from:
 * 1. the user's typed total (`typedTotal / qtyFrom`) — direct `unitFrom -> unitTo`;
 * 2. an existing mapping ({@link fromEntry}) re-expressed in the new unit via the
 *    product's conversion factors (e.g. a `st -> kg` factor becomes `st -> m²`),
 *    so re-targeting a materialized row keeps its calibrated CSV->nodon ratio;
 * 3. the product's own auto-conversion when the CSV unit *is* a product unit.
 *
 * Returns `{ quantityFactor: undefined }` when none apply (user must type a
 * value), or `null` when the quantity itself is not addressable.
 */
export const deriveQuantityMappingEntry = (params: {
  row: CandidateRow;
  product: IProduct | undefined;
  quantityName: ElementQuantityExpressionName;
  typedTotal?: number;
  fromEntry?: ResourceCodeMappingEntry;
}): ResourceCodeMappingEntry | null => {
  const { row, product, quantityName, typedTotal, fromEntry } = params;
  if (!product) return null;

  const unit = elementQuantityUnitRecord[quantityName];
  const unitTo = unit
    ? getCorrespondingConversionFactorUnit(unit, false)
    : undefined;
  if (!unitTo) return null;

  const qtyFrom = candidateQuantityFrom(row);

  if (typedTotal !== undefined && qtyFrom) {
    if (!Number.isFinite(typedTotal) || typedTotal <= 0) return null;
    return { unitTo, quantityName, quantityFactor: typedTotal / qtyFrom };
  }

  // Re-express an existing CSV->nodon factor in the newly picked unit, purely in
  // nodon space (product conversion factors). The CSV unit is irrelevant here.
  const fromUnit = fromEntry?.unitTo;
  if (
    fromEntry?.quantityFactor !== undefined &&
    fromUnit &&
    canConvertConversionFactors(product.conversion_factors, fromUnit) &&
    canConvertConversionFactors(product.conversion_factors, unitTo)
  ) {
    const converted = convertCount(
      fromEntry.quantityFactor,
      product.conversion_factors,
      fromUnit,
      unitTo,
    );
    if (Number.isFinite(converted) && converted > 0) {
      return { unitTo, quantityName, quantityFactor: converted };
    }
  }

  const auto = candidateConversion(row, product);
  // A swapped IFC measure converts from *its* column's value; the factor is
  // still keyed by the row's own qtyFrom, so materialization stays consistent.
  const autoQtyFrom = auto?.quantity ?? qtyFrom;
  if (auto && qtyFrom && autoQtyFrom) {
    const converted = convertCount(
      autoQtyFrom,
      product.conversion_factors,
      auto.unit,
      unitTo,
    );
    if (Number.isFinite(converted) && converted > 0) {
      const factor = converted / qtyFrom;
      // Same unit as the CSV: the product already expresses this natively.
      if (isIdentityConversionFactor(auto.unit, unitTo, factor)) {
        return { unitTo, quantityName };
      }
      return { unitTo, quantityName, quantityFactor: factor };
    }
  }

  return { unitTo, quantityName, quantityFactor: undefined };
};

/**
 * Mapping entry for a typed in-row total on a product row (DEV-3501). A typed
 * total must always recalibrate the conversion factor (`typedTotal / qtyFrom`)
 * so the total and the rate editor stay in sync — including products that
 * auto-convert the CSV unit, where {@link deriveMappingEntryFromQtyTo} skips
 * the factor and the two decouple. Calibrates against the picked quantity,
 * else the product's default quantity ({@link getProductDefaultQuantityName});
 * when neither resolves, falls back to a direct `unitFrom -> unitTo` factor.
 */
export const deriveTypedTotalMappingEntry = (params: {
  row: CandidateRow;
  product: IProduct | undefined;
  qtyTo: number;
  selectedQuantity: ElementQuantityExpressionName | undefined;
  fallbackUnitTo: string;
}): ResourceCodeMappingEntry | null => {
  const { row, product, qtyTo, selectedQuantity, fallbackUnitTo } = params;
  const quantityName =
    selectedQuantity ?? getProductDefaultQuantityName(product, row);
  if (quantityName) {
    const entry = deriveQuantityMappingEntry({
      row,
      product,
      quantityName,
      typedTotal: qtyTo,
    });
    if (entry) return entry;
  }
  // No resolvable quantity: recalibrate directly from the typed total. Pass no
  // product so the auto-conversion short-circuit ({}) can't decouple the total
  // from the rate — a typed total must always set the factor (DEV-3501).
  return deriveMappingEntryFromQtyTo({
    row,
    product: undefined,
    qtyTo,
    unitTo: fallbackUnitTo,
  });
};

/**
 * The element quantity a product row will materialize into. Same resolution as
 * {@link resolveProductQuantityName}: category default, unless the CSV unit
 * matches a convertible quantity (m² CSV + kg/m² product → area).
 */
export const getProductDefaultQuantityName = (
  product: IProduct | undefined,
  row?: CandidateRow,
): ElementQuantityExpressionName | undefined =>
  resolveProductQuantityName({ product, row });

/** Display unit a product row materializes into; see {@link getProductDefaultQuantityName}. */
export const getProductDefaultQuantityUnit = (
  product: IProduct | undefined,
  row?: CandidateRow,
): string | undefined => {
  const name = getProductDefaultQuantityName(product, row);
  return name ? elementQuantityUnitRecord[name] : undefined;
};

/**
 * The user's typed quantity number override on a materialized import element, or
 * `undefined` for a purely auto-derived quantity (DEV-3439). The typed total is
 * persisted as the selected quantity count's `value` (the override on top of the
 * conversion expression); a row that was only auto-converted has no such value.
 */
export const getImportQuantityOverride = (
  element: IElement,
): number | undefined => {
  const value = getSelectedElementQuantityProperty(element)?.count?.value;
  return typeof value === 'number' && Number.isFinite(value) && value > 0
    ? value
    : undefined;
};

/**
 * Build the materialization overlay for switching a mapped import row to a new
 * product/recipe while preserving the user's typed quantity (DEV-3439).
 *
 * When a `retainedQuantity` (the existing element's number override) is present,
 * it becomes both the new mapping entry's factor — so a target that can't
 * auto-convert the CSV unit still materializes to that number — and a `value`
 * override, so a target that *can* auto-convert shows the user's number instead
 * of its own conversion (and a unit change never clears it). Without an override
 * it returns a bare mapping so the new target re-derives the quantity as before.
 */
export const buildSwitchMappingOverlay = (params: {
  row: CandidateRow;
  referenceId: string;
  referenceType: MappingReferenceType;
  /** The new product, for product switches (recipes carry no conversion factors). */
  product: IProduct | undefined;
  retainedQuantity: number | undefined;
  /**
   * The row's existing mapping entry, when the new target can still address the
   * quantity it was calibrated for (see {@link isMappingCalibrationCompatible}).
   * Carrying it keeps a valid rate across the switch instead of resetting the
   * row (DEV-3456).
   */
  carriedEntry?: ResourceCodeMappingEntry;
}): { mapping: ElementOriginMapping; valueOverride: number | undefined } => {
  const {
    row,
    referenceId,
    referenceType,
    product,
    retainedQuantity,
    carriedEntry,
  } = params;
  const unitFrom = candidateUnitFrom(row);
  const baseMapping: ElementOriginMapping = {
    referenceId,
    referenceType,
    mappings: carriedEntry && unitFrom ? { [unitFrom]: carriedEntry } : {},
  };

  if (retainedQuantity === undefined || !unitFrom) {
    return { mapping: baseMapping, valueOverride: undefined };
  }

  const qtyFrom = candidateQuantityFrom(row);
  let entry: ResourceCodeMappingEntry | null;
  switch (referenceType) {
    case 'product':
      entry = deriveMappingEntryFromQtyTo({
        row,
        product,
        qtyTo: retainedQuantity,
        unitTo: product?.unit ?? '',
      });
      break;
    case 'recipe':
      entry = qtyFrom ? { quantityFactor: retainedQuantity / qtyFrom } : null;
      break;
    default:
      return assertUnreachable(referenceType);
  }

  return {
    mapping: entry
      ? { ...baseMapping, mappings: { [unitFrom]: entry } }
      : baseMapping,
    valueOverride: retainedQuantity,
  };
};

/**
 * Element quantity whose unit matches {@link measure}. Volume → `volume`,
 * Area → `area_top`/`area_side` (orientation / product default), never the
 * previously mapped dimension. Undefined when the target has no matching unit
 * (e.g. Count on a product without pcs).
 */
export const quantityNameForIfcMeasure = (
  row: CandidateRow,
  measure: IfcMeasure,
  product: IProduct | undefined,
  recipe?: Recipe,
): ElementQuantityExpressionName | undefined => {
  const target: ImportTarget | undefined = recipe
    ? { kind: 'recipe', recipe }
    : product
      ? { kind: 'product', product }
      : undefined;
  if (!target) return undefined;

  // Ask the importer, not a parallel rule set: the menu may only promise what
  // picking the column would actually do.
  const resolution = resolveImportQuantity({
    row,
    target,
    pinnedMeasure: measure,
  });
  if (resolution.status !== 'resolved') return undefined;
  return resolution.measure === measure ? resolution.quantityName : undefined;
};

/**
 * True when picking this file measure imports the row through it — the column
 * names a quantity the product/recipe can be measured in, and carries a number
 * the target can convert.
 */
export const ifcMeasureHasAutoConversion = (
  row: CandidateRow,
  measure: IfcMeasure,
  product: IProduct | undefined,
  recipe?: Recipe,
): boolean =>
  quantityNameForIfcMeasure(row, measure, product, recipe) !== undefined;

/**
 * Draft patch when the user picks a Quantity from file measure. Sets
 * {@link ImportRowDraft.ifcMeasure} and steers {@link ImportRowDraft.selectedQuantity}
 * to the matching dimension (Volume → `volume`, not leftover `area_top`).
 * When nothing can auto-convert (e.g. m³ → pcs), conversion fields are cleared
 * so the row dematerializes and the user types a new factor.
 */
export const importRowDraftPatchForIfcMeasure = (
  row: CandidateRow,
  measure: IfcMeasure,
  product: IProduct | undefined,
  recipe?: Recipe,
): Partial<ImportRowDraft> => {
  const nextName = quantityNameForIfcMeasure(row, measure, product, recipe);
  if (nextName) {
    return {
      ifcMeasure: measure,
      selectedQuantity: nextName,
      unitTo: elementQuantityUnitRecord[nextName] as ConversionFactorUnit,
      quantityFactor: undefined,
      expression: undefined,
    };
  }
  return {
    ifcMeasure: measure,
    quantityFactor: undefined,
    unitTo: undefined,
    selectedQuantity: undefined,
    totalQuantity: undefined,
    expression: undefined,
  };
};
